import type { ComponentChildren } from 'preact';
import { useDrawerEntry, closeDrawer, type DrawerPanel } from '../state/drawerStore';
import { useSwipeDownToClose } from '../hooks/useSwipeDownToClose';

export interface DrawerPanelDef {
  /** Header title shown in the shared drawer chrome. */
  title: ComponentChildren;
  /** Lazily-rendered body content — only invoked when this panel is the open one. */
  render: () => ComponentChildren;
  /** Optional gate — when false the panel is treated as absent (won't open). */
  enabled?: boolean;
}

interface Props {
  panels: Partial<Record<DrawerPanel, DrawerPanelDef>>;
  /** Close handler — defaults to the bare `closeDrawer` store action. App injects a
   *  history-aware `closeDrawerH` (pops the drawer's pushed history entry) so the Back
   *  gesture closes the drawer consistently with settings/files. */
  onClose?: () => void;
}

/**
 * DrawerHost — the ONE shared shell for every unified-drawer panel (unified-drawer.md
 * P3). Reads which panel is open from `drawerStore` (the single owner — no per-drawer
 * boolean) and slots that panel's content into the shared scrim + bottom sheet + grab
 * handle + header chrome.
 *
 * Pure renderer (SE-DR-13-no-shadow): it reads via the `useDrawerEntry` selector and
 * writes via the named `closeDrawer` action only — it holds NO component-local drawer
 * state, and emits no view-event side channel for transitions (R-A2). Triggers
 * elsewhere call `openDrawer(panel)`.
 *
 * Mount ONCE at App top level, OUTSIDE the `.view-slider` — its `will-change:transform`
 * creates a stacking context that would break the sheet's `position:fixed` (same reason
 * the other modals live there).
 */
export function DrawerHost({ panels, onClose = closeDrawer }: Props) {
  const { phase, panel } = useDrawerEntry();
  const swipe = useSwipeDownToClose(onClose);

  if (phase !== 'open' || !panel) return null;
  const def = panels[panel];
  if (!def || def.enabled === false) return null;

  return (
    <div
      class="drawer-host-scrim"
      onClick={onClose}
      // Esc-to-close: focus the scrim so a keydown lands here.
      ref={(el) => { if (el) el.focus(); }}
      tabIndex={-1}
      onKeyDown={(e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); }}
    >
      <div class="drawer-host-sheet" onClick={(e: MouseEvent) => e.stopPropagation()}>
        <div
          class="drawer-grab"
          aria-hidden="true"
          onTouchStart={swipe.onTouchStart}
          onTouchEnd={swipe.onTouchEnd}
        />
        <div class="drawer-host-header">
          <span class="drawer-host-title">{def.title}</span>
          <button type="button" class="drawer-host-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div class="drawer-host-body">{def.render()}</div>
      </div>
    </div>
  );
}
